<template>
  <div
    v-if="show"
    class="badiu-tiptap-modal"
    tabindex="-1"
    @mousedown.self="onCancel"
  >
    <div class="badiu-tiptap-modal-dialog">
      <div class="badiu-tiptap-modal-content">

        <div class="badiu-tiptap-modal-header">
          <h6 class="badiu-tiptap-modal-title">
            <i class="fas fa-image"></i>Insert / edit image
          </h6>
          <button
            type="button"
            class="badiu-tiptap-modal-close"
            aria-label="Close"
            @click="onCancel"
          >&times;</button>
        </div>

        <div class="badiu-tiptap-modal-body">

          <div class="badiu-tiptap-form-group">
            <label class="badiu-tiptap-form-label">Image URL</label>
            <div class="badiu-tiptap-input-with-action">
              <input
                v-model="form.src"
                type="text"
                class="badiu-tiptap-input"
                placeholder="https://..."
              />
              <badiu-tiptap-image-upload
                :config="config"
                @uploaded="onUploaded"
                @error="onUploadError"
              />
            </div>
          </div>

          <div class="badiu-tiptap-form-group">
            <label class="badiu-tiptap-form-label">Description (alt)</label>
            <input
              v-model="form.alt"
              type="text"
              class="badiu-tiptap-input"
              placeholder="Alternative text"
            />
          </div>

          <div class="badiu-tiptap-form-row">
            <div class="badiu-tiptap-form-col">
              <label class="badiu-tiptap-form-label">Width</label>
              <input
                v-model="form.width"
                type="text"
                class="badiu-tiptap-input"
                placeholder="auto"
              />
            </div>
            <div class="badiu-tiptap-form-col">
              <label class="badiu-tiptap-form-label">Height</label>
              <input
                v-model="form.height"
                type="text"
                class="badiu-tiptap-input"
                placeholder="auto"
              />
            </div>
          </div>

          <div
            v-if="form.src"
            class="badiu-tiptap-image-preview"
          >
            <p>Preview</p>
            <img
              :src="form.src"
              :alt="form.alt || ''"
            />
          </div>

        </div>

        <div class="badiu-tiptap-modal-footer">
          <button
            type="button"
            class="badiu-tiptap-btn badiu-tiptap-btn-secondary"
            @click="onCancel"
          >
            Cancel
          </button>

          <button
            type="button"
            class="badiu-tiptap-btn badiu-tiptap-btn-primary"
            :disabled="!form.src"
            @click="onSave"
          >
            Save
          </button>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
/**
 * BadiuTiptapImage
 *
 * Isolated modal component for inserting and editing images.
 * Does NOT handle upload directly — delegates to BadiuTiptapImageUpload.
 */
import { reactive, watch } from 'vue'
import BadiuTiptapImageUpload from './BadiuTiptapImageUpload.vue'

export default {
  name: "BadiuTiptapImage",

  components: {
    BadiuTiptapImageUpload
  },

  props: {
    show: {
      type: Boolean,
      default: false
    },

    initial: {
      type: Object,
      default: () => ({})
    },

    /** Forwarded to BadiuTiptapImageUpload (ver BadiuTiptapEditor para doc das chaves). */
    config: {
      type: Object,
      default: () => ({})
    }
  },

  emits: ["save", "cancel", "uploaded", "error"],

  setup(props, { emit }) {
    const form = reactive({
      src: "",
      alt: "",
      width: "",
      height: ""
    });

    function loadImageDimensions(src) {
      return new Promise((resolve) => {
        const img = new Image();
        img.onload  = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
        img.onerror = () => resolve(null);
        img.src = src;
      });
    }

    watch(
      () => props.show,
      (visible) => {
        if (visible) {
          form.src    = props.initial.src    || "";
          form.alt    = props.initial.alt    || "";
          form.width  = props.initial.width  || "";
          form.height = props.initial.height || "";
        }
      },
      { immediate: true }
    );

    watch(
      () => form.src,
      async (src) => {
        if (!src || form.width || form.height) return;
        const dims = await loadImageDimensions(src);
        if (dims && !form.width && !form.height) {
          form.width  = String(dims.width);
          form.height = String(dims.height);
        }
      }
    );

    function onSave() {
      if (!form.src) return;

      const data = { src: form.src, alt: form.alt || null };

      if (form.width)  data.width  = form.width;
      if (form.height) data.height = form.height;

      emit("save", data);
    }

    function onCancel() {
      emit("cancel");
    }

    async function onUploaded(payload) {
      form.src = payload.src;

      if (!form.alt && payload.name) {
        form.alt = payload.name;
      }

      const dims = await loadImageDimensions(payload.src);
      if (dims) {
        form.width  = String(dims.width);
        form.height = String(dims.height);
      }

      emit("uploaded", payload);
    }

    function onUploadError(payload) {
      emit("error", payload);
    }

    return {
      form,
      onSave,
      onCancel,
      onUploaded,
      onUploadError
    };
  }
};
</script>

<style>
/* ============================================================
 * Form controls compartilhados pelos modais (escopados).
 * Definidos aqui apenas uma vez — Image/Link/SourceCode
 * usam estas mesmas classes.
 * ============================================================ */
.badiu-tiptap-form-group {
  margin-bottom: 1rem;
}

.badiu-tiptap-form-label {
  display: block;
  margin-bottom: 0.25rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: #495057;
}

.badiu-tiptap-input,
.badiu-tiptap-select {
  display: block;
  width: 100%;
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
  line-height: 1.5;
  color: #212529;
  background-color: #fff;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  box-sizing: border-box;
  outline: none;
}

.badiu-tiptap-input:focus,
.badiu-tiptap-select:focus {
  border-color: #86b7fe;
  box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.25);
}

.badiu-tiptap-input-with-action {
  display: flex;
  gap: 0.5rem;
  align-items: flex-start;
}

.badiu-tiptap-input-with-action .badiu-tiptap-input {
  flex: 1 1 auto;
}

.badiu-tiptap-form-row {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
}

.badiu-tiptap-form-col {
  flex: 1 1 0;
  min-width: 0;
}

.badiu-tiptap-image-preview {
  text-align: center;
  padding: 1rem;
  border: 1px solid #dee2e6;
  border-radius: 0.25rem;
  background-color: #f8f9fa;
}

.badiu-tiptap-image-preview p {
  margin: 0 0 0.5rem;
  font-size: 0.85rem;
  color: #6c757d;
}

.badiu-tiptap-image-preview img {
  max-width: 100%;
  max-height: 180px;
  border-radius: 0.25rem;
}

.badiu-tiptap-btn-outline-danger {
  color: #dc3545;
  background-color: #fff;
  border-color: #dc3545;
  margin-right: auto;
}

.badiu-tiptap-btn-outline-danger:hover {
  color: #fff;
  background-color: #dc3545;
}

.badiu-tiptap-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
